import { DocumentPayloadMap } from 'bff'
import { translate } from 'i11n'
import { currencyFormat } from 'utils'
import { DocumentFooter } from '../components/DocumentFooter'
import { DocumentHeader } from '../components/DocumentHeader'
import { DocumentPage } from '../components/DocumentPage'
import { KeyValueList } from '../components/KeyValueList'
import { Section } from '../components/Section'
import { StaticTable, StaticTableColumn } from '../components/StaticTable'

type SampleDocumentProps = DocumentPayloadMap['sample']

type SampleItem = SampleDocumentProps['items'][number]

/**
 * Documento de referencia. No representa nada del negocio: existe para mostrar
 * cómo se compone un documento con los primitivos del package y para servir de
 * punto de partida al agregar uno nuevo.
 *
 * Los props salen de `sampleDocumentPayloadSchema` (`packages/bff`), que es la
 * misma forma que devuelve el resolver de la API.
 */
const SampleDocument = ({
  meta,
  summary,
  items,
  total_amount,
  notes,
}: SampleDocumentProps) => {
  const columns: StaticTableColumn<SampleItem>[] = [
    {
      key: 'description',
      header: translate('document.sample.items.description'),
      render: (item) => item.description,
    },
    {
      key: 'quantity',
      header: translate('document.sample.items.quantity'),
      align: 'right',
      render: (item) => item.quantity,
    },
    {
      key: 'unit_amount',
      header: translate('document.sample.items.unit_amount'),
      align: 'right',
      render: (item) => currencyFormat(item.unit_amount, meta.country_code),
    },
    {
      key: 'total_amount',
      header: translate('document.sample.items.total_amount'),
      align: 'right',
      render: (item) => currencyFormat(item.total_amount, meta.country_code),
    },
  ]

  return (
    <DocumentPage className='flex flex-col gap-6'>
      <DocumentHeader meta={meta} />

      <Section title={translate('document.sample.summary')}>
        <KeyValueList items={summary} />
      </Section>

      <Section title={translate('document.sample.items.title')}>
        <StaticTable
          columns={columns}
          rows={items}
          getRowKey={(item, index) => `${item.description}-${index}`}
          footer={
            <tr className='border-t border-zinc-300 font-semibold'>
              <td colSpan={3} className='py-2 text-right'>
                {translate('document.sample.total')}
              </td>
              <td className='py-2 text-right tabular-nums'>
                {currencyFormat(total_amount, meta.country_code)}
              </td>
            </tr>
          }
        />
      </Section>

      {notes && (
        <Section title={translate('document.sample.notes')}>
          <p className='whitespace-pre-line text-zinc-600'>{notes}</p>
        </Section>
      )}

      <DocumentFooter>
        {translate('document.footer_disclaimer', {
          company: meta.branding.company_name,
        })}
      </DocumentFooter>
    </DocumentPage>
  )
}

export default SampleDocument
